import { useCallback, useMemo } from 'react';

import { RedeemDataTypes } from '@src/types/bonds/nftRedemption.types';
import { useQueryClient } from '@tanstack/react-query';
import { waitForTransactionReceipt } from '@wagmi/core';
import { Address, ContractFunctionReturnType } from 'viem';
import { useReadContract, useReadContracts } from 'wagmi';

import { useAuth } from '@contexts/common/AuthorizationContext';

import { config } from '@tools/wagmi';

import { mapWagmiError } from '@helpers/common/error.helpers';

import { useWriteContractExtraGas } from '@utils/contract.utils';

import showToast from '@components/common/Toast';

import { NFT_REDEMPTION } from '@constants/bonds/contracts.constants';
import { CHAIN_ID } from '@constants/common/chain.constants';
import { SuccessMessages } from '@constants/common/messages.constants';
import { ToastType } from '@constants/common/toast.constants';

import abi from '@abi/bonds/nftRedemption';

type RedeemDataParams = {
  uuid: bigint;
  nonce: bigint;
  seller: Address;
  amountNfts: bigint;
  totalSellPrice: bigint;
  deadline: bigint;
  signature: Address;
};

export const useCreateRedeem = (handleUpdate?: () => Promise<void>) => {
  const { chainId, account } = useAuth();
  const queryClient = useQueryClient();

  const { writeContractAsync, ...rest } = useWriteContractExtraGas({
    config,
    mutation: {
      onSuccess: async (data) => {
        const transactionReceipt = await waitForTransactionReceipt(config, {
          hash: data,
        });

        // Refresh global cache (redeem table / balances) after a new request.
        await queryClient.invalidateQueries();

        if (handleUpdate) await handleUpdate();

        showToast(
          {
            title: SuccessMessages.Created,
            withLink: {
              hash: transactionReceipt.transactionHash,
              chainId: chainId as number,
            },
          },
          ToastType.Success,
        );
      },
      onError: (e) => {
        console.log('useCreateRedeem:', e);
        showToast({ title: mapWagmiError(e) }, ToastType.Error);
      },
    },
  });

  const onWriteContract = useCallback(
    async (redeemData: RedeemDataParams) => {
      await writeContractAsync({
        abi,
        address: NFT_REDEMPTION,
        functionName: 'create',
        chainId,
        args: [redeemData],
        account,
      });
    },
    [chainId, writeContractAsync, account],
  );

  return {
    writeContractAsync: onWriteContract,
    ...rest,
  };
};

export const useGetUuidsCount = (chainId?: CHAIN_ID, isAdmin?: boolean) => {
  const { account } = useAuth();

  return useReadContract({
    address: NFT_REDEMPTION,
    abi,
    functionName: 'uuidsCount',
    chainId,
    query: {
      enabled: !!chainId && !!account && isAdmin,
    },
  });
};

export const useGetUuids = (chainId?: CHAIN_ID, isAdmin?: boolean) => {
  const { account } = useAuth();
  const { data: uuidsCount, refetch: refetchUuidsCount } = useGetUuidsCount(
    chainId,
    isAdmin,
  );

  const uuidsArray = useMemo(() => {
    if (!uuidsCount) return [];

    return Array.from({ length: Number(uuidsCount) }, (_, i) => BigInt(i));
  }, [uuidsCount]);

  const { data, ...rest } = useReadContracts({
    contracts:
      uuidsArray.length && chainId
        ? uuidsArray.map((uuid) => ({
            address: NFT_REDEMPTION,
            abi,
            functionName: 'uuids',
            args: [uuid],
            chainId,
            account,
          }))
        : [],
    query: {
      enabled: !!chainId && !!uuidsArray.length && isAdmin,
    },
    allowFailure: false,
  });
  return {
    ...rest,
    data,
    refetch: refetchUuidsCount,
  };
};

export const useGetUserUuidsCount = (chainId?: CHAIN_ID, isAdmin?: boolean) => {
  const { account } = useAuth();

  return useReadContract({
    address: NFT_REDEMPTION,
    abi,
    functionName: 'userToUUIDSCount',
    chainId,
    args: [account as Address],
    query: {
      enabled: !!chainId && !!account && !isAdmin,
    },
  });
};

export const useGetUserUuids = (chainId?: CHAIN_ID, isAdmin?: boolean) => {
  const { account } = useAuth();
  const { data: uuidsCount, refetch: refetchUuidsCount } = useGetUserUuidsCount(
    chainId,
    isAdmin,
  );

  const uuidsArray = useMemo(() => {
    if (!uuidsCount) return [];

    return Array.from({ length: Number(uuidsCount) }, (_, i) => BigInt(i));
  }, [uuidsCount]);

  const { data, ...rest } = useReadContracts({
    contracts:
      uuidsArray.length && chainId
        ? uuidsArray.map((uuid) => ({
            address: NFT_REDEMPTION,
            abi,
            functionName: 'userToUUIDS',
            args: [uuid, account as Address],
            chainId,
            account,
          }))
        : [],
    query: {
      enabled: !!chainId && !!uuidsArray.length && !isAdmin,
    },
    allowFailure: false,
  });
  return {
    ...rest,
    data,
    refetch: refetchUuidsCount,
  };
};

type RedeemReturn = ContractFunctionReturnType<
  typeof abi,
  'view',
  'redeemData'
>;

type GetRedeemDataParamTypes = {
  chainId?: CHAIN_ID;
  isAdmin?: boolean;
};

export const useGetRedeemData = ({
  chainId,
  isAdmin,
}: GetRedeemDataParamTypes) => {
  const { data: userUuids, refetch: refetchUserUuids } = useGetUserUuids(
    chainId,
    isAdmin,
  );
  const { data: adminUuids, refetch: refetchAdminUuids } = useGetUuids(
    chainId,
    isAdmin,
  );

  const uuids = isAdmin ? adminUuids : userUuids;

  const {
    data,
    refetch: refetchRedeemData,
    isPending,
    ...rest
  } = useReadContracts({
    contracts:
      uuids && chainId
        ? uuids.map((uuid) => ({
            address: NFT_REDEMPTION,
            abi,
            functionName: 'redeemData',
            args: [uuid],
            chainId,
          }))
        : [],
    query: {
      enabled: !!chainId && !!uuids?.length,
      select: (res): RedeemDataTypes[] => {
        return res.map((item: unknown) => {
          const [uuid, timestamp, seller, amountNfts, totalSellPrice, status] =
            item as RedeemReturn;
          return {
            uuid,
            timestamp,
            seller,
            amountNfts,
            totalSellPrice,
            status,
          };
        });
      },
    },
    allowFailure: false,
  });

  const handleUpdate = async () => {
    await Promise.all([await refetchAdminUuids(), await refetchRedeemData()]);
  };

  return {
    ...rest,
    data,
    refetch: isAdmin ? handleUpdate : refetchUserUuids,
    isPending: !!chainId && !!uuids?.length ? isPending : false,
  };
};

export const useRedeem = (handleUpdate?: () => Promise<void>) => {
  const { chainId, account } = useAuth();

  const { writeContractAsync, ...rest } = useWriteContractExtraGas({
    config,
    mutation: {
      onSuccess: async (data) => {
        const transactionReceipt = await waitForTransactionReceipt(config, {
          hash: data,
        });

        if (handleUpdate) await handleUpdate();

        showToast(
          {
            title: SuccessMessages.Redeemed,
            withLink: {
              hash: transactionReceipt.transactionHash,
              chainId: chainId as number,
            },
          },
          ToastType.Success,
        );
      },
      onError: (e) => {
        console.log('useRedeem:', e);
        showToast({ title: mapWagmiError(e) }, ToastType.Error);
      },
    },
  });

  const onWriteContract = useCallback(
    async ({ uuids, value }: { uuids: bigint[]; value: bigint }) => {
      await writeContractAsync({
        abi,
        address: NFT_REDEMPTION,
        functionName: 'redeem',
        chainId,
        args: [uuids],
        value,
        account,
      });
    },
    [chainId, writeContractAsync, account],
  );

  return {
    writeContractAsync: onWriteContract,
    ...rest,
  };
};

export const useCancel = (handleUpdate?: () => Promise<void>) => {
  const { chainId, account } = useAuth();

  const { writeContractAsync, ...rest } = useWriteContractExtraGas({
    config,
    mutation: {
      onSuccess: async (data) => {
        const transactionReceipt = await waitForTransactionReceipt(config, {
          hash: data,
        });

        if (handleUpdate) await handleUpdate();

        showToast(
          {
            title: SuccessMessages.Canceled,
            withLink: {
              hash: transactionReceipt.transactionHash,
              chainId: chainId as number,
            },
          },
          ToastType.Success,
        );
      },
      onError: (e) => {
        console.log('useRedeem:', e);
        showToast({ title: mapWagmiError(e) }, ToastType.Error);
      },
    },
  });

  const onWriteContract = useCallback(
    async (uuids: bigint[]) => {
      await writeContractAsync({
        abi,
        address: NFT_REDEMPTION,
        functionName: 'cancel',
        chainId,
        args: [uuids],
        account,
      });
    },
    [chainId, writeContractAsync, account],
  );

  return {
    writeContractAsync: onWriteContract,
    ...rest,
  };
};
